/**
 * <nss-quick-add data-modal="quick-add-{id}" data-product-url="/products/handle">
 *   <button class="js-quick-add">Quick Add</button>
 * </nss-quick-add>
 *
 * On click it fetches the `nss-quick-modal` section (no preload) via the Section
 * Rendering API, injects the returned <nss-modal> and opens it. Cached by DOM,
 * so a second click just reopens.
 */
// Air Reviews collection-rating widget (the compact .areviews_product_item stars).
// collection_rating.js runs once at page load, so the modal's element — injected
// later via AJAX — stays empty. areviewsCollectionReviews() is the app's official
// re-init for AJAX-added cards: it re-scans the DOM and fills unrendered boxes.
// If it isn't available, fall back to cloning the already-rendered stars from the
// product's card elsewhere on the page.
function initAReviews(modal) {
  if (!modal) return;
  const items = modal.querySelectorAll('.areviews_product_item');
  if (!items.length) return;

  items.forEach((el) => {
    const pid = el.dataset.productId;
    if (!pid || el.innerHTML.trim() !== '') return;

    const source = Array.prototype.slice
      .call(document.querySelectorAll('.areviews_stars' + pid))
      .find((s) => s !== el && s.innerHTML.trim() !== '');

    if (source) el.innerHTML = source.innerHTML;
  });
}

if (!customElements.get("nss-quick-add")) {
  customElements.define(
    "nss-quick-add",
    class NSSQuickAdd extends HTMLElement {
      constructor() {
        super();

        this.section = "nss-quick-modal";
        this.modalId = this.getAttribute("data-modal");
        this.productUrl = this.getAttribute("data-product-url");

        this.trigger = this.querySelector(".js-quick-add");
        this.loading = false;

        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        if (this.trigger) this.trigger.addEventListener("click", this.onClick);
      }

      disconnectedCallback() {
        if (this.trigger) this.trigger.removeEventListener("click", this.onClick);
      }

      onClick(e) {
        e.preventDefault();
        this.open();
      }

      sectionUrl() {
        const sep = this.productUrl.indexOf("?") > -1 ? "&" : "?";
        return this.productUrl + sep + "sections=" + this.section;
      }

      showModal(modal) {
        if (typeof modal.openDialog === "function") {
          modal.openDialog();
        } else {
          modal.classList.add("is-visible");
          document.documentElement.classList.add("is-nss-modal-show");
        }
        initAReviews(modal);

        const root = modal.querySelector(".nss-quick-add");
        if (root) this.syncPlan(root);
      }

      open() {
        if (!this.modalId || !this.productUrl) return;

        // Already injected → reopen without refetch
        const existing = document.querySelector(`nss-modal[data-modal="${this.modalId}"]`);
        if (existing) {
          this.showModal(existing);
          return;
        }

        if (this.loading) return;
        this.loading = true;
        this.trigger.classList.add("is-loading");
        this.trigger.setAttribute("aria-busy", "true");

        fetch(this.sectionUrl(), { headers: { "X-Requested-With": "XMLHttpRequest" } })
          .then((res) => {
            if (!res.ok) throw new Error("Quick Add fetch failed: " + res.status);
            return res.json();
          })
          .then((data) => {
            const html = data && data[this.section];
            if (!html) throw new Error("Quick Add: section markup missing");

            const tpl = document.createElement("template");
            tpl.innerHTML = html.trim();
            const modal = tpl.content.querySelector("nss-modal");
            if (!modal) throw new Error("Quick Add: <nss-modal> not found in section");

            document.body.appendChild(modal); // custom element upgrades here
            this.bindVariantSwitch(modal);

            // Paint the off-screen start state first so the slide-up runs on the
            // first open instead of being skipped.
            modal.getBoundingClientRect();
            setTimeout(() => this.showModal(modal), 30);
          })
          .catch((err) => {
            console.error("[nss-quick-add]", err);
          })
          .finally(() => {
            this.loading = false;
            this.trigger.classList.remove("is-loading");
            this.trigger.removeAttribute("aria-busy");
          });
      }

      // Variant option radios → resolve the matching variant and swap gallery /
      // summary / plan select / button. Plan select → update displayed name.
      // Both then re-sync price + the hidden selling_plan input.
      bindVariantSwitch(modal) {
        modal.addEventListener("change", (e) => {
          const root = e.target.closest(".nss-quick-add");
          if (!root) return;

          if (e.target.closest(".nss-quick-add__variants")) {
            this.onVariantChange(root);
            return;
          }

          const planSelect = e.target.closest("[nss-qa-plan-select]");
          if (planSelect) {
            const opt = planSelect.options[planSelect.selectedIndex];
            const block = planSelect.closest(".nss-quick-add__plan");
            const nameEl = block && block.querySelector("[nss-qa-plan-name]");
            if (nameEl && opt) nameEl.textContent = opt.textContent.trim();
            this.syncPlan(root);
          }
        });
      }

      // Match the selected option values to a variant (no URL change / no PDP
      // re-render) and reflect it across the modal.
      onVariantChange(root) {
        const dataEl = root.querySelector("[nss-qa-variants]");
        if (!dataEl) return;

        let variants;
        try {
          variants = JSON.parse(dataEl.textContent);
        } catch (err) {
          return;
        }

        const selected = Array.prototype.map.call(
          root.querySelectorAll(".nss-quick-add__variant-group"),
          (fs) => {
            const checked = fs.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
          }
        );

        const variant = variants.find((v) =>
          v.options.every((opt, i) => opt === selected[i])
        );
        if (!variant) return;

        const id = String(variant.id);

        // Cart variant id.
        const idInput = root.querySelector("[nss-qa-variant-id]");
        if (idInput) idInput.value = id;

        // Gallery.
        root.querySelectorAll("nss-buybox-gallery-variant").forEach((gv) => {
          const active = gv.getAttribute("nss-variant-id") === id;
          gv.classList.toggle("nss-active", active);
          // Inactive variants' Swipers init at 0 width while hidden — re-measure.
          if (active && gv.swiper && gv.swiper.main) gv.swiper.main.update();
        });

        // Summary header (title / pills / description).
        root.querySelectorAll(".nss-quick-add__summary").forEach((s) => {
          s.classList.toggle("nss-active", s.getAttribute("data-variant-id") === id);
        });

        // Plan select.
        root.querySelectorAll(".nss-quick-add__plan").forEach((p) => {
          p.classList.toggle("nss-active", p.getAttribute("data-variant-id") === id);
        });

        // Sold-out variants stay selectable — reflect availability in the button.
        const atc = root.querySelector(".nss-quick-add__atc");
        if (atc) {
          atc.disabled = !variant.available;
          const span = atc.querySelector("span");
          if (span) span.textContent = variant.available ? "Add To Cart" : "Sold Out";
        }

        this.syncPlan(root);
      }

      // Reflect the active variant's selected plan into the displayed price and
      // the hidden selling_plan input (disabled for One-Time Purchase so it isn't
      // submitted as an empty value).
      syncPlan(root) {
        const activePlan =
          root.querySelector(".nss-quick-add__plan.nss-active") ||
          root.querySelector(".nss-quick-add__plan");
        const select = activePlan && activePlan.querySelector("[nss-qa-plan-select]");
        if (!select) return;

        const opt = select.options[select.selectedIndex];
        if (!opt) return;

        const priceEl = root.querySelector("[nss-qa-price-current]");
        const compareEl = root.querySelector("[nss-qa-price-compare]");
        const planInput = root.querySelector("[nss-qa-selling-plan]");

        if (priceEl) priceEl.textContent = opt.getAttribute("data-price-money") || "";
        if (compareEl) compareEl.textContent = (opt.getAttribute("data-compare-money") || "").trim();

        if (planInput) {
          const val = select.value;
          planInput.value = val;
          planInput.disabled = val === "";
        }
      }
    }
  );
}
